import React, { useCallback, useMemo, useRef } from 'react';
import { Alert, Card, Spin } from 'antd';
import { useIntl } from 'react-intl';
import { AgGridReact } from 'ag-grid-react';
import { ColDef, GridApi, GridReadyEvent } from 'ag-grid-community';
import { PartnerIdentity } from './ObtPartnerDetailsTab';
import {
    Obt_SearchSurveyHistoryByCompanyCodePartnerNoQuery,
    useObt_SearchSurveyHistoryByCompanyCodePartnerNoQuery,
} from '../obtSurveyQueries.generated';

// ─── Types ──────────────────────────────────────────────────────────────────────

type SurveyRow = NonNullable<
    Obt_SearchSurveyHistoryByCompanyCodePartnerNoQuery['obtSearchSurveyHistoryByCompanyCodePartnerNo']
>[number];

type ObtSurveyDetailsTabProps = {
    partnerIdentity: PartnerIdentity;
};

// ─── Date formatter ─────────────────────────────────────────────────────────────

function formatDateValue(params: { value?: string | null }): string {
    if (!params.value) return '';
    const d = new Date(params.value);
    if (Number.isNaN(d.getTime())) return params.value;
    return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

// ─── Component ──────────────────────────────────────────────────────────────────

const ObtSurveyDetailsTab: React.FC<ObtSurveyDetailsTabProps> = ({ partnerIdentity }) => {
    const intl = useIntl();
    const gridApiRef = useRef<GridApi | null>(null);

    const { partnerno, companycode, category, systemCode } = partnerIdentity;
    const shouldSkip = !partnerno || !companycode || !category || !systemCode;

    const { data, loading, error } = useObt_SearchSurveyHistoryByCompanyCodePartnerNoQuery({
        variables: { partnerNo: partnerno, companyCode: companycode, category, systemCode },
        skip: shouldSkip,
        fetchPolicy: 'network-only',
    });

    // Filter out rows where surveyId <= 0
    const rowData: SurveyRow[] = useMemo(() => {
        const raw = data?.obtSearchSurveyHistoryByCompanyCodePartnerNo ?? [];
        return raw.filter((r) => (r.surveyId ?? 0) > 0);
    }, [data]);

    // ─── Column definitions ─────────────────────────────────────────────────────
    const columnDefs: ColDef<SurveyRow>[] = useMemo(
        () => [
            {
                headerName: intl.formatMessage({ id: 'obt.survey.col.surveyId', defaultMessage: 'Survey ID' }),
                field: 'surveyId',
                width: 110,
                sortable: true,
                filter: true,
            },
            {
                headerName: intl.formatMessage({ id: 'obt.survey.col.surveyType', defaultMessage: 'Survey Type' }),
                field: 'surveyType',
                width: 140,
                sortable: true,
                filter: true,
            },
            {
                headerName: intl.formatMessage({ id: 'obt.survey.col.channelDataAcceptedDate', defaultMessage: 'Document Type Submit Date' }),
                field: 'channelDataAcceptedDate',
                width: 200,
                sortable: true,
                valueFormatter: formatDateValue,
            },
            {
                headerName: intl.formatMessage({ id: 'obt.survey.col.agreementAcceptedDate', defaultMessage: 'Agreement Details Accepted Date' }),
                field: 'agreementAcceptedDate',
                width: 240,
                sortable: true,
                valueFormatter: formatDateValue,
            },
            {
                headerName: intl.formatMessage({ id: 'obt.survey.col.surveySubmissionDate', defaultMessage: 'Service Provider Submit Date' }),
                field: 'surveySubmissionDate',
                width: 220,
                sortable: true,
                valueFormatter: formatDateValue,
            },
            {
                headerName: intl.formatMessage({ id: 'obt.survey.col.provisionalGoLiveDate', defaultMessage: 'Prov Go-live Date' }),
                field: 'provisionalGoLiveDate',
                width: 170,
                sortable: true,
                valueFormatter: formatDateValue,
            },
            {
                headerName: intl.formatMessage({ id: 'obt.survey.col.confirmedGoLiveDate', defaultMessage: 'Confirmed Go-live Date' }),
                field: 'confirmedGoLiveDate',
                width: 190,
                sortable: true,
                valueFormatter: formatDateValue,
            },
            {
                headerName: intl.formatMessage({ id: 'obt.survey.col.insertTime', defaultMessage: 'Survey Created' }),
                field: 'insertTime',
                width: 160,
                sortable: true,
                valueFormatter: formatDateValue,
            },
            {
                headerName: intl.formatMessage({ id: 'obt.survey.col.surveyStatus', defaultMessage: 'Survey Status' }),
                field: 'surveyStatus',
                width: 140,
                sortable: true,
                filter: true,
            },
            {
                headerName: intl.formatMessage({ id: 'obt.survey.col.insertUser', defaultMessage: 'Survey Creation User' }),
                field: 'insertUser',
                width: 170,
                sortable: true,
                filter: true,
            },
        ],
        [intl],
    );

    const defaultColDef: ColDef = useMemo(
        () => ({
            resizable: true,
            sortable: true,
        }),
        [],
    );

    const onGridReady = useCallback((event: GridReadyEvent) => {
        gridApiRef.current = event.api;
        event.api.sizeColumnsToFit();
    }, []);

    // ─── Render states ──────────────────────────────────────────────────────────
    if (shouldSkip) {
        return (
            <Alert
                type="warning"
                showIcon={true}
                message={intl.formatMessage({
                    id: 'obt.survey.missingParams',
                    defaultMessage: 'Missing partner identifiers. Cannot load survey history.',
                })}
            />
        );
    }

    if (error) {
        return (
            <Alert
                type="error"
                showIcon={true}
                message={intl.formatMessage({ id: 'obt.survey.loadError', defaultMessage: 'Failed to load survey history.' })}
                description={error.message}
            />
        );
    }

    return (
        <Card
            title={intl.formatMessage({ id: 'obt.survey.card.title', defaultMessage: 'Survey History' })}
            size="small"
        >
            <Spin spinning={loading}>
                {!loading && rowData.length === 0 ? (
                    <Alert
                        type="info"
                        showIcon={true}
                        message={intl.formatMessage({
                            id: 'obt.survey.empty',
                            defaultMessage: 'No survey history found for this partner.',
                        })}
                    />
                ) : (
                    <div className="ag-theme-balham" style={{ width: '100%', height: 500 }}>
                        <AgGridReact<SurveyRow>
                            rowData={rowData}
                            columnDefs={columnDefs}
                            defaultColDef={defaultColDef}
                            onGridReady={onGridReady}
                            animateRows={true}
                            pagination={true}
                            paginationAutoPageSize={true}
                            suppressCellFocus={true}
                        />
                    </div>
                )}
            </Spin>
        </Card>
    );
};

export default ObtSurveyDetailsTab;
